import React, { useId, useMemo } from "react";

import Box, { BoxProps } from "../Box";
import Text from "../Text";
import useBoolean from "../../hooks/useBoolean";

import DropdownButton from "../DropdownButton/DropdownButton";
import PopupMenu from "../PopupMenu";
import factory from "../../testUtils/factory";
import Button from "../Button";
import Tooltip from "../Tooltip";

export type SelectOption<ValueType> = {
  value: ValueType;
  label: string;
  labelNode?: React.ReactNode;
};

type Props<ValueType> = {
  label?: string;
  size?: "small" | "regular";
  buttonStyle?: "dropdown" | "tertiary";
  value: ValueType;
  options: (SelectOption<ValueType> | "-")[];
  onChange?: (value: ValueType) => void;
  width?: string;
  disabled?: boolean;
  menuId?: string;
  tooltip?: string | React.ReactNode;
  placeholder?: string;
} & BoxProps;

/**
 * A custom select component that wraps a Button and PopupMenu
 * Just provide options and an onChange handle similar to a native select
 */
function Select<ValueType>({
  label,
  value,
  buttonStyle = "dropdown",
  size = "regular",
  options,
  onChange,
  width,
  disabled,
  menuId: _menuId,
  tooltip,
  placeholder,
  ...rest
}: Props<ValueType>) {
  const [isMenuActive, showMenu, hideMenu] = useBoolean(false),
    genId = useId(),
    menuId = useMemo(() => _menuId ?? genId, [_menuId, genId]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const displayValue = (options.filter(f => f !== "-") as SelectOption<ValueType>[]).find(
    f => f.value === value
  )?.label;
  function renderButton() {
    const sharedProps = {
      "data-popup-source": menuId,
      onClick: showMenu,
      text: displayValue ?? placeholder,
      size,
      disabled,
    };
    return buttonStyle === "dropdown" ? (
      <DropdownButton styleOverrides={{ width }} {...sharedProps} />
    ) : (
      <Button buttonStyle="tertiary" icon="action.expand" {...sharedProps} />
    );
  }

  const menuItems = useMemo(() => {
    return options.map(opt => {
      if (opt === "-") {
        return <Box borderBottom="default" height="1px" key={factory.genUUID()} />;
      }
      return (
        <PopupMenu.Item
          key={opt.value.toString()}
          title={opt.labelNode || opt.label}
          checked={value === opt.value}
          onClick={() => onChange(opt.value)}
        />
      );
    });
  }, [onChange, options, value]);

  return (
    <Box {...rest} ref={containerRef}>
      {label && (
        <Text
          as="label"
          data-testid="select-label"
          display="block"
          color="black"
          mb="4px"
          htmlFor={menuId}
          textAlign="left">
          {label}
        </Text>
      )}
      {tooltip && (
        <Tooltip source={menuId} direction={Tooltip.Direction.Top}>
          {tooltip}
        </Tooltip>
      )}
      <Box data-tooltip-source={menuId}>{renderButton()}</Box>
      <PopupMenu
        source={menuId}
        open={isMenuActive}
        // value={value}
        close={hideMenu}
        width={containerRef?.current?.clientWidth > 236 ? containerRef?.current?.clientWidth : 236}>
        {menuItems}
      </PopupMenu>
    </Box>
  );
}

export default Select;
